import { produce } from "immer";
import find from "lodash/find";
import React, { useContext, useEffect, useMemo } from "react";
import { DndProvider } from "react-dnd";
import { TouchBackend } from "react-dnd-touch-backend";
import shortid from "shortid";

import { scoreGraphicGapBySpot } from "@examspark/score";
import { Stem, StemGraphicGapMatch } from "@examspark/types";

import {
  addTokenOnDrop,
  hideTokenAfterUse,
  replaceTokenOnDrop,
} from "../common/drag/dndUtils";
import { Action } from "../context/reducer";
import QuestionContext from "../QuestionContext";

import ImageMap, { DroppableType } from "./common/image-map/ImageMap";
import GraphicToken from "./shared/GraphicToken";
import Prompt from "./shared/Prompt";
import TextToken, { DndItem } from "./shared/Token";

interface Props {
  data: StemGraphicGapMatch;
}

type GraphicGapMatchResponse = {
  drops: Array<Stem.Drop>;
  gaps: Stem.ResponseGaps;
};

const GraphicGapMatch: React.FC<React.PropsWithChildren<Props>> = (props) => {
  const [player, dispatchPlayer] = useContext(QuestionContext);

  useRevealKey(props, player, dispatchPlayer);

  // graphic/text drop locations and their current values
  const { drops, marks } = calculateAndMarkDroppables(props, player);

  // inline-text stype gaps' current values
  const gaps: Stem.ResponseGaps = player?.response?.[props.data.id]?.gaps ?? {};

  // tokens that are still available to be used
  const filteredTokens = calculateFilteredTokens(props, player);

  const graphicTokens = useMemo(
    () => filteredTokens.filter((t) => t.type === "graphic"),
    [filteredTokens],
  );
  const textTokens = useMemo(
    () => filteredTokens.filter((t) => t.type === "text"),
    [filteredTokens],
  );

  const handleDrop = (gapId: string, item: DndItem) => {
    let rsp: GraphicGapMatchResponse = {
      drops: [],
      gaps: {},
    };

    if (player?.response?.[props.data.id]) {
      rsp = player.response[props.data.id];
    }

    rsp = produce(rsp, (draft) => {
      // if you drag a token on top of a filled spot, swap the tokens
      const spot = props.data.content.spots?.find((s) => s.id === gapId);
      if (spot?.allowMultipleTokens) {
        draft.drops = addTokenOnDrop(gapId, item, draft.drops);
      } else {
        draft.drops = replaceTokenOnDrop(gapId, item, draft.drops);
      }
    });

    dispatchPlayer({
      type: "RESPONSE",
      data: {
        id: props.data.id,
        response: rsp,
      },
    });
  };

  const handleRemove = (gapId: string, item: DndItem) => {
    let rsp: GraphicGapMatchResponse = { ...player.response?.[props.data.id] };
    const drop = rsp.drops.find(
      (d) => d.draggableId === item.id && d.droppableId === gapId,
    );

    rsp = produce(rsp, (draft) => {
      draft.drops = draft.drops.filter(
        (d) => d.instanceId !== drop?.instanceId,
      );
    });

    dispatchPlayer({
      type: "RESPONSE",
      data: {
        id: props.data.id,
        response: rsp,
      },
    });
  };

  const handleTextGapChange = (gapId: string, value: string) => {
    let rsp: GraphicGapMatchResponse = {
      drops: [],
      gaps: {},
    };

    if (player && player.response && player.response[props.data.id]) {
      rsp = player.response[props.data.id];
    }

    rsp = produce(rsp, (draft) => {
      draft.gaps[gapId] = value;

      return draft;
    });

    dispatchPlayer({
      type: "RESPONSE",
      data: {
        id: props.data.id,
        response: rsp,
      },
    });
  };

  return (
    <div className="item-player-element item-player-stem">
      <Prompt data={props.data.content.prompt} />
      {/* @ts-expect-error children */}
      <DndProvider
        backend={TouchBackend}
        options={{
          enableMouseEvents: true,
          enableHoverOutsideTarget: true,
        }}
        context={window}
      >
        {props.data.content.image && (
          <div className="item-player-stage">
            <ImageMap
              USE_NEW_DRAG
              image={props.data.content.image}
              droppables={props.data.content.spots}
              drops={drops}
              gappables={props.data.content.gaps}
              gaps={gaps}
              marks={marks}
              onTokenDrop={handleDrop}
              onTokenRemove={handleRemove}
              onTextGapChange={handleTextGapChange}
            />
          </div>
        )}

        {!!filteredTokens?.length && (
          <div className="item-player-tokens">
            <div className="graphic-tokens">
              {graphicTokens.map((t) => (
                <GraphicToken
                  key={t.item.id}
                  useDragType="token"
                  data={t.item}
                />
              ))}
            </div>
            <div className="text-tokens">
              {textTokens.map((t) => (
                <TextToken key={t.item.id} useDragType="token" data={t.item} />
              ))}
            </div>
          </div>
        )}
      </DndProvider>
    </div>
  );
};
export default GraphicGapMatch;

const useRevealKey = (
  props: Props,
  player: any,
  dispatchPlayer: React.Dispatch<Action>,
) => {
  useEffect(() => {
    if (player.revealKey) {
      // maybe use a map? its an array, we dont need to access via for in
      let drops: Array<Stem.Drop> = [];
      const gaps: Stem.ResponseGaps = {};

      // add all correct answers to the response
      props.data.scoring?.pairs?.forEach((p) => {
        if (p.dst && p.src && p.isKey) {
          drops.push({
            draggableId: p.src,
            droppableId: p.dst,
            instanceId: shortid.generate(),
          });
        }
      });

      const tokens: DroppableType[] = [];
      props.data.content.options?.forEach((o) => {
        tokens.push({
          type: "text",
          item: o,
        });
      });
      props.data.content.tokens?.forEach((t) => {
        tokens.push({
          type: "graphic",
          item: t,
        });
      });

      // ensure we only have one response in gaps that don't allow multiple tokens
      // and that we respect any token's maxUsage
      drops = drops.reduce((acc, cur) => {
        const allowMultipleTokens = props.data.content.spots?.find(
          (s) => s.id === cur.droppableId,
        )?.allowMultipleTokens;
        const gapIsFilled = acc.some((a) => a.droppableId === cur.droppableId);

        if (allowMultipleTokens || !gapIsFilled) {
          // ensure the token is still available to use
          const token = hideTokenAfterUse(tokens, acc).find(
            (t) => t.item.id === cur.draggableId,
          );
          if (token) {
            acc.push(cur);
          }
        }
        return acc;
      }, [] as Array<Stem.Drop>);

      // score text_gap
      for (const spot of props.data.content.spots ?? []) {
        // skip non-text gaps
        if (spot.shape !== "text_gap") continue;
        const gapId = spot.id;

        const gap = props.data.scoring?.gaps?.[gapId];
        const gapContent = props.data.content.gaps?.[gapId];
        let keyFound = false;

        if (gap?.options) {
          for (const optionId in gap.options) {
            if (!keyFound) {
              if (gap.options[optionId].isKey) {
                keyFound = true;
                console.log("scoring gap", gapContent);
                switch (gapContent?.type) {
                  case "inline-choice":
                    gaps[gapId] = optionId;
                    break;
                  case "text-entry":
                  case "math-entry":
                  case "numeric-entry":
                    // we need the text of the option
                    gaps[gapId] =
                      find(props.data.content.gaps?.[gapId].options, [
                        "id",
                        optionId,
                      ])?.body ?? "";
                    break;
                }
              }
            }
          }
        }
      }

      // todo: adjust mark for for shape type/size
      dispatchPlayer({
        type: "RESPONSE",
        data: {
          id: props.data.id,
          score: true,
          response: {
            drops,
            gaps,
          },
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [player.revealKey]);
};

const calculateFilteredTokens = (props: Props, player: any) => {
  const response: GraphicGapMatchResponse | undefined =
    player?.response?.[props.data.id];

  // collect all our tokens into a discriminated union
  const tokens: DroppableType[] = [
    ...(props.data.content.tokens ?? []).map((t) => ({
      type: "graphic" as const,
      item: t,
    })),
    ...(props.data.content.options ?? []).map((o) => ({
      type: "text" as const,
      item: o,
    })),
  ];

  const filteredTokens = hideTokenAfterUse(tokens, response?.drops ?? []);

  return filteredTokens;
};

const calculateAndMarkDroppables = (props: Props, player: any) => {
  const response: GraphicGapMatchResponse | undefined =
    player?.response?.[props.data.id];

  // Collect responses into something we can use
  const drops: Record<string, Array<DroppableType>> = {};
  response?.drops.forEach((d: Stem.Drop) => {
    if (!drops[d.droppableId]) {
      drops[d.droppableId] = [];
    }

    const graphicToken = props.data.content.tokens?.find(
      (t) => t.id === d.draggableId,
    );
    const textToken = props.data.content.options?.find(
      (t) => t.id === d.draggableId,
    );

    if (graphicToken) {
      drops[d.droppableId].push({
        type: "graphic",
        item: graphicToken,
        drop: d,
      });
    }

    if (textToken) {
      drops[d.droppableId].push({
        type: "text",
        item: textToken,
        drop: d,
      });
    }
  });

  let marks: Record<string, Stem.Mark> = {};
  if (player.scoreResponses) {
    marks = Object.fromEntries(
      Object.entries(
        scoreGraphicGapBySpot({
          expected: props.data,
          response,
          scoring: props.data.scoring,
        }),
      ).map(([spotId, isCorrect]) => [
        spotId,
        isCorrect ? "correct" : "incorrect",
      ]),
    );
  }

  return { drops, marks };
};
